import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { normalizeError } from '@/lib/normalizeError';
import { guardAndCoalescePatches, MAX_PATCH_ITEMS } from '@/lib/patchGuard';

interface FileItem {
  name: string;
  path: string;
  isFolder: boolean;
  size: number;
  updatedAt: string;
}

interface Project {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  status?: 'building' | 'ready';
}

const EDGE_FETCH_COOLDOWN_MS = 10000;

export function useCodeWorkspace() {
  const { user } = useAuth();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const lastEdgeFetchErrorRef = useRef<number>(0);

  const callEdgeFunction = useCallback(async (action: string, params: Record<string, unknown> = {}) => {
    if (Date.now() - lastEdgeFetchErrorRef.current < EDGE_FETCH_COOLDOWN_MS) {
      throw new Error('Edge function temporarily unavailable. Please try again in a moment.');
    }
    const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !anonKey) throw new Error('Missing Supabase config');

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not signed in. Sign in to call this function.');
    let accessToken = session.access_token;

    const doFetch = async (token: string) => {
      return fetch(`${supabaseUrl}/functions/v1/code-workspace-files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action, ...params }),
      });
    };

    const url = `${supabaseUrl}/functions/v1/code-workspace-files`;
    try {
      let response = await doFetch(accessToken);
      if (response.status === 401) {
        await supabase.auth.refreshSession();
        const { data: { session: newSession } } = await supabase.auth.getSession();
        if (!newSession) throw new Error('Session expired. Please sign in again.');
        response = await doFetch(newSession.access_token);
      }
      const requestId =
        response.headers.get('x-request-id') ||
        response.headers.get('cf-ray') ||
        response.headers.get('supabase-request-id') ||
        null;
      const responseText = await response.text();
      const bodySnippet = responseText.slice(0, 1500);
      let data: Record<string, unknown> = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        // leave data as {}
      }
      const resolvedRequestId = requestId ?? (typeof data?.requestId === 'string' ? data.requestId : null);
      if (!response.ok) {
        console.error(
          '[edge] non-2xx url=', url,
          'status=', response.status,
          'requestId=', resolvedRequestId ?? '(none)',
          'body=', bodySnippet,
        );
        const errFromBody = typeof data?.error === 'string' ? data.error : bodySnippet;
        const reqIdStr = resolvedRequestId ? ` requestId=${resolvedRequestId}` : '';
        throw new Error(`HTTP ${response.status}: ${errFromBody}${reqIdStr}`);
      }
      if (!response.ok) {
        const errFromBody = typeof data?.error === 'string' ? data.error : bodySnippet;
        const errMessage = `HTTP ${response.status}: ${errFromBody}${reqIdStr}`;
        throw new Error(errMessage);
      }
      if (data?.success === false) {
        return {
          success: false,
          summary: (typeof data?.error === 'string' ? data.error : null) || (typeof data?.summary === 'string' ? data.summary : null) || 'Edge function returned success:false',
          results: Array.isArray(data?.results) ? data.results as Array<{ filePath: string; action: string; success: boolean; error?: string }> : [],
        };
      }
      if (data?.error) throw data.error;
      return data;
    } catch (err: unknown) {
      console.error('[edge] fetch threw', err);
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('FunctionsFetchError') || msg.includes('Failed to send a request')) {
        lastEdgeFetchErrorRef.current = Date.now();
      }
      throw err;
    }
  }, []);

  const listProjects = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await callEdgeFunction('listProjects');
      const projectsWithStatus = (data.projects || []).map((p: Project) => ({
        ...p,
        status: 'ready' as const,
      }));
      setProjects(projectsWithStatus);
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error listing projects:', e.name, e.message, e.raw);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [user, callEdgeFunction]);

  // Direct fetch a project by ID (bypasses cache, ensures we get the latest)
  const fetchProjectById = useCallback(async (projectId: string): Promise<Project | null> => {
    if (!user) {
      console.log('[useCodeWorkspace] FETCH_PROJECT_BY_ID: No user');
      return null;
    }
    console.log('[useCodeWorkspace] FETCH_PROJECT_BY_ID:', projectId);
    
    try {
      const { data, error } = await supabase
        .from('code_projects')
        .select('id, name, description, created_at, updated_at, deploy_url')
        .eq('id', projectId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('[useCodeWorkspace] FETCH_PROJECT_BY_ID_ERROR:', error);
        return null;
      }
      
      if (!data) {
        console.log('[useCodeWorkspace] FETCH_PROJECT_BY_ID: Project not found or not accessible');
        return null;
      }
      
      console.log('[useCodeWorkspace] FETCH_PROJECT_BY_ID_SUCCESS:', data.id, data.name);
      return { ...data, status: 'ready' as const };
    } catch (err) {
      const e = normalizeError(err);
      console.error('[useCodeWorkspace] FETCH_PROJECT_BY_ID_EXCEPTION:', e.name, e.message);
      return null;
    }
  }, [user]);

  const createProject = useCallback(async (name: string, description?: string) => {
    if (!user) return null;
    setLoading(true);
    try {
      const data = await callEdgeFunction('createProject', { name, description });
      console.log('[useCodeWorkspace] CREATE_PROJECT_RESULT:', data?.project);
      toast.success('Project created');
      await listProjects();
      return data.project;
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error creating project:', e.name, e.message, e.raw);
      toast.error(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, callEdgeFunction, listProjects]);

  const selectProject = useCallback(async (project: Project, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    console.log('[useCodeWorkspace] SELECT_PROJECT:', project.id, project.name, silent ? '(silent)' : '');
    setCurrentProject(project);
    setLoading(true);
    try {
      // Retry up to 5 times if no files found (scaffold might still be saving)
      let fileList: FileItem[] = [];
      let retries = 0;
      const maxRetries = 5;
      const retryDelay = 1000; // 1 second

      while (retries < maxRetries) {
        const data = await callEdgeFunction('listFiles', { projectId: project.id });
        fileList = data.files || [];

        if (fileList.length > 0) {
          break; // Files found, continue
        }

        retries++;
        if (retries < maxRetries) {
          console.log(`[DB FETCH] projectId=${project.id} rows=0, retrying in ${retryDelay}ms (attempt ${retries}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }

      setFiles(fileList);
      const paths = fileList.map((f: any) => f.path ?? f.name);
      console.log(`[DB FETCH] projectId=${project.id} rows=${fileList.length} paths=`, paths.slice(0, 15));

      if (fileList.length === 0) {
        console.warn(`[DB FETCH] No files found after ${maxRetries} retries for project ${project.id}`);
      }
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error listing files:', e.name, e.message, e.raw);
      // Only show toast for user-initiated requests, not background polling
      if (!silent) {
        toast.error(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [callEdgeFunction]);

  // Select project by ID - fetches directly if not in cache
  const selectProjectById = useCallback(async (projectId: string): Promise<boolean> => {
    console.log('[useCodeWorkspace] SELECT_PROJECT_BY_ID:', projectId);
    
    // First check if we already have it in the projects list
    const cachedProject = projects.find(p => p.id === projectId);
    if (cachedProject) {
      console.log('[useCodeWorkspace] SELECT_PROJECT_BY_ID: Found in cache');
      await selectProject(cachedProject);
      return true;
    }
    
    // Not in cache, fetch directly from database
    console.log('[useCodeWorkspace] SELECT_PROJECT_BY_ID: Not in cache, fetching directly...');
    const project = await fetchProjectById(projectId);
    
    if (project) {
      // Add to projects list and select it
      setProjects(prev => {
        const exists = prev.find(p => p.id === project.id);
        if (exists) return prev;
        return [...prev, project];
      });
      await selectProject(project);
      return true;
    }
    
    console.log('[useCodeWorkspace] SELECT_PROJECT_BY_ID: Project not found');
    return false;
  }, [projects, fetchProjectById, selectProject]);

  const renameProject = useCallback(async (projectId: string, newName: string) => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from('code_projects')
        .update({ name: newName, updated_at: new Date().toISOString() })
        .eq('id', projectId)
        .eq('user_id', user.id);

      if (error) throw error;

      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, name: newName, updated_at: new Date().toISOString() } : p
      ));
      
      if (currentProject?.id === projectId) {
        setCurrentProject(prev => prev ? { ...prev, name: newName } : null);
      }

      toast.success('Project renamed');
      return true;
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error renaming project:', e.name, e.message, e.raw);
      toast.error(e.message);
      return false;
    }
  }, [user, currentProject]);

  const deleteProject = useCallback(async (projectId: string) => {
    if (!user) return false;
    try {
      // Delete files from storage first
      const { data: files } = await supabase.storage
        .from('code-workspaces')
        .list(`${user.id}/${projectId}`);

      if (files && files.length > 0) {
        const filePaths = files.map(f => `${user.id}/${projectId}/${f.name}`);
        await supabase.storage.from('code-workspaces').remove(filePaths);
      }

      // Delete project record
      const { error } = await supabase
        .from('code_projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', user.id);

      if (error) throw error;

      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setFiles([]);
      }

      toast.success('Project deleted');
      return true;
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error deleting project:', e.name, e.message, e.raw);
      toast.error(e.message);
      return false;
    }
  }, [user, currentProject]);

  const duplicateProject = useCallback(async (projectId: string) => {
    if (!user) return null;
    setLoading(true);
    try {
      // Get original project
      const originalProject = projects.find(p => p.id === projectId);
      if (!originalProject) throw new Error('Project not found');

      // Create new project
      const newProject = await createProject(
        `${originalProject.name} (Copy)`,
        originalProject.description || undefined
      );

      if (!newProject) throw new Error('Failed to create duplicate');

      // Copy files
      const { data: originalFiles } = await supabase.storage
        .from('code-workspaces')
        .list(`${user.id}/${projectId}`);

      if (originalFiles && originalFiles.length > 0) {
        for (const file of originalFiles) {
          const { data: fileData } = await supabase.storage
            .from('code-workspaces')
            .download(`${user.id}/${projectId}/${file.name}`);

          if (fileData) {
            await supabase.storage
              .from('code-workspaces')
              .upload(`${user.id}/${newProject.id}/${file.name}`, fileData);
          }
        }
      }

      toast.success('Project duplicated');
      await listProjects();
      return newProject;
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error duplicating project:', e.name, e.message, e.raw);
      toast.error(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, projects, createProject, listProjects]);

  const listFiles = useCallback(async () => {
    if (!user || !currentProject) return;
    setLoading(true);
    try {
      const data = await callEdgeFunction('listFiles', { projectId: currentProject.id });
      setFiles(data.files || []);
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error listing files:', e.name, e.message, e.raw);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [user, currentProject, callEdgeFunction]);

  const loadFile = useCallback(async (path: string): Promise<string | null> => {
    if (!user || !currentProject) return null;
    try {
      const data = await callEdgeFunction('loadFile', { projectId: currentProject.id, path });
      return data.content;
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error loading file:', e.name, e.message, e.raw);
      toast.error(e.message);
      return null;
    }
  }, [user, currentProject, callEdgeFunction]);

  const saveFile = useCallback(async (path: string, content: string): Promise<boolean> => {
    if (!user || !currentProject) return false;
    setSaving(true);
    try {
      await callEdgeFunction('saveFile', { projectId: currentProject.id, path, content });
      
      // Update project's updated_at
      await supabase
        .from('code_projects')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', currentProject.id);

      return true;
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error saving file:', e.name, e.message, e.raw);
      toast.error(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [user, currentProject, callEdgeFunction]);

  const deleteFile = useCallback(async (path: string): Promise<boolean> => {
    if (!user || !currentProject) return false;
    try {
      await callEdgeFunction('deleteFile', { projectId: currentProject.id, path });
      toast.success('File deleted');
      await listFiles();
      return true;
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error deleting file:', e.name, e.message, e.raw);
      toast.error(e.message);
      return false;
    }
  }, [user, currentProject, callEdgeFunction, listFiles]);

  const applyEdits = useCallback(async (
    edits: Array<{
      filePath: string;
      action: 'create' | 'modify' | 'delete';
      content?: string;
    }>,
    options?: { isInitialBuild?: boolean }
  ): Promise<{ success: boolean; summary: string; results: Array<{ filePath: string; action: string; success: boolean; error?: string }>; appliedPaths?: string[]; errorCode?: string; maxPatchItems?: number }> => {
    if (!user || !currentProject) {
      return { success: false, summary: 'No project selected', results: [], appliedPaths: [] };
    }

    const { edits: guardedEdits, error, errorCode, maxPatchItems } = guardAndCoalescePatches(edits, MAX_PATCH_ITEMS, { isInitialBuild: options?.isInitialBuild });
    if (error) return { success: false, summary: error, results: [], appliedPaths: [], errorCode, maxPatchItems };

    try {
      const data = await callEdgeFunction('applyEdits', {
        projectId: currentProject.id,
        edits: guardedEdits,
        isInitialBuild: options?.isInitialBuild ?? false,
      });
      
      const results = Array.isArray(data?.results) ? data.results : [];
      const appliedPaths = results.filter((r: { success?: boolean }) => r.success).map((r: { filePath?: string }) => r.filePath).filter(Boolean) as string[];

      if (data.success) {
        toast.success(data.summary);
      } else {
        toast.warning(data.summary);
      }
      
      // Refresh file list after applying edits
      await listFiles();
      
      return { ...data, appliedPaths };
    } catch (error) {
      const e = normalizeError(error);
      console.error('Error applying edits:', e.name, e.message, e.raw);
      toast.error(e.message);
      return { success: false, summary: 'Failed to apply changes', results: [], appliedPaths: [] };
    }
  }, [user, currentProject, callEdgeFunction, listFiles]);

  return {
    files,
    projects,
    currentProject,
    loading,
    saving,
    listProjects,
    createProject,
    selectProject,
    selectProjectById,
    fetchProjectById,
    renameProject,
    deleteProject,
    duplicateProject,
    listFiles,
    loadFile,
    saveFile,
    deleteFile,
    applyEdits,
  };
}
